// src/pages/Preisverleihung.jsx
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { ChevronRight, ChevronLeft, Settings, AlertTriangle, Projector, Users } from "lucide-react";
import Button from "../components/ui/Button";
import { toast } from "react-toastify";
import { loadCachedTeamsAndStations } from "../utils/loadCachedData";
import { teamKey } from "../utils/teamKey";
import { gleichstandsHinweise, rankingHatPlaetze, nachRang } from "../utils/gleichstand";
import { preiseVerteilen, slotBeschreibung, gleichstandPraefix } from "../utils/preisvergabe";
import DoppeltrefferPanel from "../components/DoppeltrefferPanel";
import GleichstandPanel from "../components/GleichstandPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Plaetze und Gleichstaende kommen fertig aus dem Backend
// (functions/services/rangliste.js). BEHOBENER FEHLER: Diese Seite rechnete
// frueher selbst (Punkte, dann Stationsanzahl), die Urkunden dagegen nur nach
// Punkten – punktgleiche Teams wurden hier als Platz 2 und 3 aufgerufen,
// waehrend beide Urkunden "Platz 2" trugen.

const MITGLIEDER = ["member1", "member2", "member3", "member4", "member5"];

export default function Preisverleihung() {
  const [teams, setTeams] = useState([]);
  const [filterEvent, setFilterEvent] = useState("Erwachsene");
  const [currentIndex, setCurrentIndex] = useState(null);
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startetGerade, setStartetGerade] = useState(false);
  // Doppeltreffer: teamKey -> Slot-ID des Preises, den das Orga-Team dem Team
  // zuspricht (utils/preisvergabe.js). Gespeichert im Backend
  // (/awards/entscheidungen) – frueher nur im Seitenzustand, ein Neuladen oder
  // ein Klick auf "Preise" loeschte alles. Beim Start wird nach dem Abgleich
  // neu gerechnet; ein neuer Doppeltreffer stoppt den Start.
  const [entscheidungen, setEntscheidungen] = useState({});
  // Details eingeklappt: Die Startseite kann auf dem Beamer laufen.
  const [doppeltrefferAufgeklappt, setDoppeltrefferAufgeklappt] = useState(false);
  const [gleichstandAufgeklappt, setGleichstandAufgeklappt] = useState(false);
  // Wird die Siegerehrung auf den Beamer projiziert? Dann keine Details, die
  // noch nicht aufgerufene Plaetze verraten (Namen punktgleicher Teams,
  // Nachrueck-Hinweise, Doppeltreffer). Bewusst bei jedem Oeffnen wieder
  // "Beamer": vergessen umzustellen soll nie etwas verraten.
  const [beamer, setBeamer] = useState(true);

  const navigate = useNavigate();
  const firstLoadRef = useRef(true);

  // === 1️⃣ HARDCODED NEULADEN – ohne Cache ===
  const fetchData = useCallback(async () => {
    try {
      if (firstLoadRef.current) setLoading(true);

      console.log("[Preisverleihung] Lade Daten (Hard Reload) …");

      // BEHOBENER FEHLER: Hier stand `true` als drittes Argument. Die Funktion
      // erwartet ein Options-Objekt – `true` ergab alle Flags `false`, die
      // Siegerehrung lief also still aus dem localStorage-Cache statt mit dem
      // aktuellen Stand.
      const { teams: teamsData, results } = await loadCachedTeamsAndStations(
        BACKEND_URL,
        navigate,
        { reloadTeams: true, reloadResults: true }
      );

      const resultsTeams = results?.teams || [];

      const mergedTeams = (teamsData || []).map((team) => {
        // Zuordnung ueber die stabile Team-ID; Rueckfall auf den Namen fuer
        // Ranking-Dokumente aus der Zeit vor der Umstellung.
        const result =
          resultsTeams.find((r) => (r.team_id ?? r.team_name) === teamKey(team)) || {};
        return {
          ...team,
          total_points: result.total_points ?? 0,
          age_bonus: result.age_bonus ?? 0,
          stations_completed: result.stations_completed ?? 0,
          rank: result.rank ?? null,
          rank_team_type: result.rank_team_type ?? null,
          gleichstand: result.gleichstand ?? null,
        };
      });

      setTeams(mergedTeams);

      const pricesData = await fetchWithAuth(`${BACKEND_URL}/awards`, navigate).catch(() => []);
      setPrices(pricesData || []);

      setLoading(false);
      firstLoadRef.current = false;
      console.log(`[Preisverleihung] ${mergedTeams.length} Teams geladen.`);
      return { teams: mergedTeams, prices: pricesData || [] };
    } catch (err) {
      console.error("[Preisverleihung] Fehler beim Laden:", err);
      setLoading(false);
      return null;
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Gespeicherte Doppeltreffer-Entscheidungen einmalig laden.
  useEffect(() => {
    fetchWithAuth(`${BACKEND_URL}/awards/entscheidungen`, navigate)
      .then((d) => setEntscheidungen(d?.entscheidungen || {}))
      .catch((err) => {
        console.error("[Preisverleihung] Entscheidungen nicht geladen:", err);
        toast.error(
          "Gespeicherte Doppeltreffer-Entscheidungen konnten nicht geladen werden – " +
            "bitte die Seite neu laden, bevor neu entschieden wird."
        );
      });
  }, [navigate]);

  // Entscheidungen sofort anzeigen und speichern. Scheitert das Speichern,
  // gilt die Entscheidung nur bis zum Neuladen – darauf wird hingewiesen.
  const entscheidungenSpeichern = async (neu) => {
    setEntscheidungen(neu);
    try {
      await fetchWithAuth(`${BACKEND_URL}/awards/entscheidungen`, navigate, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ entscheidungen: neu }),
      });
    } catch (err) {
      console.error("[Preisverleihung] Entscheidungen nicht gespeichert:", err);
      toast.error(
        `Entscheidung nicht gespeichert (${err.message}) – sie gilt nur, bis die Seite neu geladen wird.`,
        { autoClose: 8000 }
      );
    }
  };

  // === 2️⃣ Reihenfolge der Siegerehrung (letzter Platz zuerst) ===
  const rankedTeams = useMemo(() => {
    return teams
      .filter((t) => t.event_type === filterEvent)
      .sort(nachRang)
      .map((team) => ({ ...team, eventRank: team.rank, teamTypeRank: team.rank_team_type }))
      .reverse();
  }, [teams, filterEvent]);

  // Aelteres Ranking-Dokument ohne zentrale Plaetze: nicht still mit falschen
  // Plaetzen weitermachen, sondern deutlich darauf hinweisen.
  const plaetzeFehlen = !rankingHatPlaetze(teams);

  const currentTeam =
    currentIndex !== null && rankedTeams[currentIndex]
      ? rankedTeams[currentIndex]
      : null;

  const totalTeamsInEvent = rankedTeams.length;
  const achievedEventRank =
    currentTeam?.eventRank > 0 ? currentTeam.eventRank : totalTeamsInEvent;
  const teamTypeRankDisplay =
    currentTeam?.teamTypeRank > 0 ? currentTeam.teamTypeRank : 1;

  // === 3️⃣ Preiszuordnung ===
  // Gleichstandshinweise zum aktuellen Team (Kategorieplatz).
  // Teams ohne Punkte (nicht angetreten) sind zwangslaeufig alle gleichauf –
  // das ist keine Warnung wert und wuerde nur eine lange Namensliste zeigen.
  // (Dieselbe Regel wie in der Gleichstandsliste der Exportseite.)
  // Auf dem Beamer ohne die Namen der punktgleichen Teams (siehe `beamer`).
  const gleichstand = useMemo(
    () =>
      currentTeam && (currentTeam.total_points || 0) > 0
        ? gleichstandsHinweise(currentTeam, { anonym: beamer })
        : null,
    [currentTeam, beamer]
  );

  // Jedes Team bekommt hoechstens einen Preis. Trifft es mehrere, entscheidet
  // das Orga-Team vor dem Start (DoppeltrefferPanel); der andere Preis rueckt
  // in seiner Wertung nach.
  const verteilung = useMemo(
    () => preiseVerteilen(teams, prices, filterEvent, entscheidungen),
    [teams, prices, filterEvent, entscheidungen]
  );

  const currentZuteilung = currentTeam ? verteilung.zuteilung.get(teamKey(currentTeam)) : null;
  const currentPrice = currentZuteilung?.slot.preis ?? null;

  // === 4️⃣ UI ===
  if (loading) {
    return (
      <div className="p-6 text-white bg-secondary-950 min-h-screen">
        <h1 className="text-2xl font-bold mb-4">Preisverleihung</h1>
        <p className="text-gray-400">Lade Daten...</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 bg-secondary-950 text-white min-h-screen flex flex-col">
      {/* Kopfzeile */}
      <div className="flex justify-between items-center mb-6 border-b border-secondary-800 pb-4">
        <h1 className="text-2xl font-bold text-primary-500">Siegerehrung</h1>
        <Button
          onClick={() => navigate("/preise-verwalten")}
          variant="secondary"
          className="flex items-center gap-1 text-sm sm:text-base px-3 py-1"
        >
          <Settings className="w-4 h-4 sm:w-5 sm:h-5" />
          Preise
        </Button>
      </div>

      {/* Kategorieauswahl */}
      <div className="flex gap-3 mb-8">
        {["Erwachsene", "Kinder"].map((cat) => (
          <Button
            key={cat}
            onClick={() => {
              setFilterEvent(cat);
              setCurrentIndex(null);
            }}
            variant={filterEvent === cat ? "primary" : "secondary"}
            className="text-sm sm:text-base px-4 py-2"
          >
            {cat}
          </Button>
        ))}
      </div>

      {plaetzeFehlen && (
        <div className="mb-6 flex items-start gap-2 p-3 rounded border border-red-700 bg-red-900/30 text-red-200 text-sm">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <span>
            Das Ranking enthält noch keine berechneten Plätze (älterer Stand). Bitte ein
            Ergebnis oder den Altersbonus erneut speichern, damit neu berechnet wird –
            sonst stimmen die Plätze nicht.
          </span>
        </div>
      )}

      {/* Start / Inhalt */}
      {!currentTeam ? (
        <div className="flex flex-col items-center justify-center flex-1 text-center">
          {/* Anzeige: Beamer (Publikum sieht mit) oder nur Orga-Team. */}
          <div className="w-full max-w-2xl mb-6 text-left">
            <p className="text-sm font-semibold text-gray-400 mb-2">Anzeige</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" role="radiogroup">
              {[
                {
                  wert: true,
                  icon: <Projector className="w-5 h-5 shrink-0" />,
                  titel: "Auf dem Beamer",
                  text: "Das Publikum sieht mit. Keine Namen punktgleicher Teams, keine Nachrück- oder Doppeltreffer-Details.",
                },
                {
                  wert: false,
                  icon: <Users className="w-5 h-5 shrink-0" />,
                  titel: "Nur Orga-Team",
                  text: "Alle Details – nicht projizieren, sonst sind kommende Plätze verraten.",
                },
              ].map((o) => (
                <button
                  key={String(o.wert)}
                  type="button"
                  role="radio"
                  aria-checked={beamer === o.wert}
                  onClick={() => {
                    setBeamer(o.wert);
                    if (o.wert) {
                      setDoppeltrefferAufgeklappt(false);
                      setGleichstandAufgeklappt(false);
                    }
                  }}
                  className={`flex items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
                    beamer === o.wert
                      ? "border-primary-500 bg-primary-500/10"
                      : "border-secondary-700 bg-secondary-900 hover:border-secondary-500"
                  }`}
                >
                  <span className={beamer === o.wert ? "text-primary-400" : "text-gray-400"}>{o.icon}</span>
                  <span>
                    <span className="block font-semibold text-white">{o.titel}</span>
                    <span className="block text-xs text-gray-400 mt-0.5">{o.text}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <GleichstandPanel
            verteilung={verteilung}
            aufgeklappt={gleichstandAufgeklappt}
            onUmschalten={() => setGleichstandAufgeklappt((a) => !a)}
            onWahl={(schluessel, key) => entscheidungenSpeichern({ ...entscheidungen, [schluessel]: key })}
            onZuruecksetzen={() => {
              const praefix = gleichstandPraefix(filterEvent);
              entscheidungenSpeichern(
                Object.fromEntries(
                  Object.entries(entscheidungen).filter(([key]) => !key.startsWith(praefix))
                )
              );
            }}
            detailsErlaubt={!beamer}
          />
          <DoppeltrefferPanel
            verteilung={verteilung}
            aufgeklappt={doppeltrefferAufgeklappt}
            onUmschalten={() => setDoppeltrefferAufgeklappt((a) => !a)}
            onWahl={(key, slotId) => entscheidungenSpeichern({ ...entscheidungen, [key]: slotId })}
            onZuruecksetzen={() => {
              // Nur die Entscheidungen der gerade gewaehlten Kategorie – die
              // der anderen bleiben gespeichert.
              const dieseKategorie = new Set(
                teams.filter((t) => t.event_type === filterEvent).map((t) => teamKey(t))
              );
              entscheidungenSpeichern(
                Object.fromEntries(
                  Object.entries(entscheidungen).filter(([key]) => !dieseKategorie.has(key))
                )
              );
            }}
            detailsErlaubt={!beamer}
          />
          <p className="text-lg text-gray-400 mb-6">
            Starte mit dem letzten Platz ({totalTeamsInEvent}. Platz im Event{" "}
            <strong>{filterEvent}</strong>)
          </p>
          <Button
            onClick={async () => {
              setStartetGerade(true);
              try {
                // Vor der Siegerehrung: Stationsdaten Eintrag fuer Eintrag mit
                // den Einzelergebnissen abgleichen und neu rechnen lassen
                // (functions/services/stationsdaten.js). Scheitert das, geht
                // es mit dem vorhandenen Stand weiter – aber mit Hinweis.
                try {
                  const r = await fetchWithAuth(`${BACKEND_URL}/results/abgleich`, navigate, {
                    method: "POST",
                  });
                  if (r?.abweichungen?.length > 0) {
                    toast.warn(
                      `Vor dem Start wurden Unstimmigkeiten an ${r.abweichungen.length} Station(en) ` +
                        "gefunden und korrigiert – die Plätze sind jetzt aktuell.",
                      { autoClose: 8000 }
                    );
                  }
                } catch (err) {
                  toast.error(
                    `Abgleich vor dem Start nicht möglich (${err.message}). Es wird der zuletzt ` +
                      "berechnete Stand verwendet.",
                    { autoClose: 8000 }
                  );
                }
                const daten = await fetchData();
                if (!daten) return;
                const anzahl = daten.teams.filter((t) => t.event_type === filterEvent).length;
                if (anzahl === 0) return;
                // Der Abgleich kann Plaetze verschoben haben: Ist dadurch ein
                // neuer Doppeltreffer offen, nicht starten. Die Entscheidung
                // verschiebt Preise bei Teams, die frueher aufgerufen werden.
                const v = preiseVerteilen(daten.teams, daten.prices, filterEvent, entscheidungen);
                if (v.offen || v.gleichstandOffen) {
                  toast.warn(
                    "Nach dem Abgleich ist eine Entscheidung offen (Doppeltreffer oder Gleichstand) – " +
                      "das Orga-Team muss erst entscheiden.",
                    { autoClose: 8000 }
                  );
                  return;
                }
                setCurrentIndex(0);
              } finally {
                setStartetGerade(false);
              }
            }}
            className="px-6 py-3 text-lg bg-primary-500 text-black font-bold rounded"
            disabled={
              totalTeamsInEvent === 0 || startetGerade || !!verteilung.offen || !!verteilung.gleichstandOffen
            }
          >
            {startetGerade ? "Prüfe Ergebnisse…" : "🏁 Siegerehrung starten"}
          </Button>
          {(verteilung.offen || verteilung.gleichstandOffen) && (
            <p className="mt-3 text-sm text-red-300">
              Erst starten, wenn das Orga-Team alle Doppeltreffer und Gleichstände entschieden hat.
            </p>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center flex-1 w-full">
          {/* Gewaehlte Anzeige dezent bzw. (Orga) deutlich kennzeichnen */}
          {beamer ? (
            <p className="mb-3 flex items-center gap-1.5 text-xs text-gray-500">
              <Projector className="w-3.5 h-3.5" /> Beamer-Ansicht
            </p>
          ) : (
            <p className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-red-300">
              <Users className="w-4 h-4" /> Orga-Ansicht mit allen Details – nicht projizieren
            </p>
          )}

          {/* Platz / Team Box */}
          <div className="w-full max-w-sm sm:max-w-xl bg-secondary-900 border border-secondary-800 rounded-lg p-6 text-center shadow-2xl transition-all duration-300 transform scale-100">
            {gleichstand && (
              <div
                className={`mb-4 text-left text-sm sm:text-base p-3 rounded border ${
                  gleichstand.offen
                    ? "text-red-200 bg-red-900/30 border-red-700"
                    : "text-yellow-100 bg-yellow-900/30 border-yellow-600"
                }`}
              >
                <div className="flex items-center gap-2 font-semibold mb-1">
                  <AlertTriangle className="w-5 h-5 shrink-0" />
                  <span>
                    {gleichstand.offen
                      ? `Gleichstand auf Platz ${achievedEventRank} – Platz geteilt!`
                      : "Punktgleichheit – per Stationsplätzen entschieden"}
                  </span>
                </div>
                <ul className="space-y-0.5">
                  {gleichstand.hinweise.map((h, i) => (
                    <li key={i}>• {h.text}</li>
                  ))}
                </ul>
              </div>
            )}

            <h2 className="text-4xl sm:text-5xl font-extrabold mb-4 text-yellow-500">
              Platz {achievedEventRank}
            </h2>

            <p className="text-xl sm:text-3xl text-primary-400 font-semibold mb-4 truncate w-full">
              {currentTeam.team_name}
            </p>

            {/* Teammitglieder. BEHOBENER FEHLER: gelesen wurden `member1_name`
                usw. – die Felder heissen `member1` bis `member5`, die Namen
                erschienen deshalb nie. */}
            {MITGLIEDER.some((k) => currentTeam[k]) && (
              <div className="mt-3 text-sm text-gray-300">
                <p className="font-semibold text-gray-400 mb-1">Teammitglieder:</p>
                <ul className="space-y-1">
                  {MITGLIEDER.map((k) => currentTeam[k])
                    .filter(Boolean)
                    .map((name, i) => (
                      <li key={i}>• {name}</li>
                    ))}
                </ul>
              </div>
            )}

            <div className="flex justify-center gap-4 text-sm sm:text-base mb-4 mt-4">
              <p className="text-gray-400">Typ: {currentTeam.team_type}</p>
              {currentTeam.team_type !== "Alle" && (
                <p className="text-gray-400">
                  ({teamTypeRankDisplay}. in {currentTeam.team_type})
                </p>
              )}
            </div>

            <p className="text-2xl font-bold text-yellow-400 border-t border-secondary-700 pt-4 mt-4">
              {currentTeam.total_points} Punkte
            </p>

            {currentPrice ? (
              <div className="bg-yellow-500/20 border-2 border-yellow-500 rounded-lg p-5 mt-6">
                <p className="text-xs sm:text-sm uppercase tracking-widest text-yellow-200/80 mb-1">
                  Euer Preis
                </p>
                <h3 className="text-3xl sm:text-5xl font-extrabold text-yellow-400 mb-2">
                  🎁 {currentPrice.title}
                </h3>
                {currentPrice.description && (
                  <p className="text-base sm:text-xl text-gray-200">
                    {currentPrice.description}
                  </p>
                )}
                {currentPrice.sponsor && (
                  <p className="text-base sm:text-xl text-yellow-100 mt-3 pt-3 border-t border-yellow-500/40">
                    Gesponsert von{" "}
                    <strong className="text-yellow-300">{currentPrice.sponsor}</strong>
                  </p>
                )}
                {/* Verraet die eigentliche Platzierung in einer anderen
                    Wertung – deshalb nur in der Orga-Ansicht. */}
                {currentZuteilung.nachgerueckt && !beamer && (
                  <p className="text-xs sm:text-sm text-yellow-200/80 mt-2">
                    Nachgerückt: {slotBeschreibung(currentZuteilung.slot)} (eigentlich{" "}
                    {currentZuteilung.bisherPlatz}.) – ein besser platziertes Team hat einen
                    anderen Preis bekommen.
                  </p>
                )}
              </div>
            ) : (
              <p className="text-gray-500 italic mt-5">Kein Preis vergeben</p>
            )}
          </div>

          {/* Navigation */}
          <div className="flex gap-4 mt-8 w-full max-w-sm sm:max-w-xl">
            <Button
              onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
              disabled={currentIndex === 0}
              variant="secondary"
              className="flex items-center justify-center gap-1 text-sm sm:text-base flex-1"
            >
              <ChevronLeft /> Zurück
            </Button>
            {currentIndex < totalTeamsInEvent - 1 ? (
              <Button
                onClick={() => setCurrentIndex((i) => i + 1)}
                className="flex items-center justify-center gap-1 bg-primary-500 text-black flex-1 text-sm sm:text-base"
              >
                Weiter <ChevronRight />
              </Button>
            ) : (
              <Button
                onClick={() => setCurrentIndex(null)}
                className="bg-green-600 hover:bg-green-500 text-black flex-1 text-sm sm:text-base"
              >
                🎉 Siegerehrung beenden
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
